// LD-4 — useLadderCycle hook.
//
// Reads the current LADDER cycle via get_ld_current_cycle RPC.
// Returns cycle metadata + 5 rungs (R1-R5) with status.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ────────────────────────────────────────────────

export interface LdRung {
  id: string;
  rung_code: string;
  rung_label: string;
  target_odds_min: number;
  target_odds_max: number | null;
  actual_odds: number;
  selection_count: number;
  expected_hit_rate: number;
  status: "pending" | "live" | "won" | "lost" | "void";
  selections_won: number;
  selections_lost: number;
  selections_void: number;
  selections_pending: number | null;
}

export interface LdCycle {
  id: string;
  cycle_type: "weekend" | "midweek";
  status: string;
  fixture_window_start: string;
  fixture_window_end: string;
  pool_size: number | null;
  pool_avg_confidence: number | null;
  pool_avg_clv: number | null;
  pool_diversity_score: number | null;
  rungs_published_at: string | null;
  created_at: string;
}

export interface UseLadderCycleState {
  cycle: LdCycle | null;
  rungs: readonly LdRung[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Hook ─────────────────────────────────────────────────

export function useLadderCycle(): UseLadderCycleState {
  const { user } = useAuthSession();
  const [cycle, setCycle] = useState<LdCycle | null>(null);
  const [rungs, setRungs] = useState<readonly LdRung[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setCycle(null);
      setRungs([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const { data, error: rpcError } = await (
          client.rpc as unknown as (fn: string) => Promise<{
            data: Record<string, unknown> | null;
            error: { message: string } | null;
          }>
        )("get_ld_current_cycle");

        if (cancelled) return;

        if (rpcError) {
          setError(rpcError.message);
          setLoading(false);
          return;
        }

        if (!data) {
          setCycle(null);
          setRungs([]);
          setLoading(false);
          return;
        }

        const raw = data as Record<string, unknown>;

        // Parse cycle
        if (raw.cycle && typeof raw.cycle === "object") {
          const c = raw.cycle as Record<string, unknown>;
          setCycle({
            id: String(c.id ?? ""),
            cycle_type: (c.cycle_type as LdCycle["cycle_type"]) ?? "weekend",
            status: String(c.status ?? "initialising"),
            fixture_window_start: String(c.fixture_window_start ?? ""),
            fixture_window_end: String(c.fixture_window_end ?? ""),
            pool_size: typeof c.pool_size === "number" ? c.pool_size : null,
            pool_avg_confidence: typeof c.pool_avg_confidence === "number" ? c.pool_avg_confidence : null,
            pool_avg_clv: typeof c.pool_avg_clv === "number" ? c.pool_avg_clv : null,
            pool_diversity_score: typeof c.pool_diversity_score === "number" ? c.pool_diversity_score : null,
            rungs_published_at: typeof c.rungs_published_at === "string" ? c.rungs_published_at : null,
            created_at: String(c.created_at ?? ""),
          });
        } else {
          setCycle(null);
        }

        // Parse rungs
        if (Array.isArray(raw.rungs)) {
          setRungs(
            (raw.rungs as Record<string, unknown>[]).map((r) => ({
              id: String(r.id ?? ""),
              rung_code: String(r.rung_code ?? "R1"),
              rung_label: String(r.rung_label ?? "STARTER"),
              target_odds_min: Number(r.target_odds_min ?? 0),
              target_odds_max: typeof r.target_odds_max === "number" ? r.target_odds_max : null,
              actual_odds: Number(r.actual_odds ?? 0),
              selection_count: Number(r.selection_count ?? 0),
              expected_hit_rate: Number(r.expected_hit_rate ?? 0),
              status: (r.status as LdRung["status"]) ?? "pending",
              selections_won: Number(r.selections_won ?? 0),
              selections_lost: Number(r.selections_lost ?? 0),
              selections_void: Number(r.selections_void ?? 0),
              selections_pending: typeof r.selections_pending === "number" ? r.selections_pending : null,
            })),
          );
        } else {
          setRungs([]);
        }

        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, tick]);

  return useMemo(
    () => ({ cycle, rungs, loading, error, refetch }),
    [cycle, rungs, loading, error, refetch],
  );
}
